import { AbstractControl, ValidatorFn } from '@angular/forms';

/**
 * A NANP number in E.164: `+1`, an area code (NPA) starting 2-9, a central office code (NXX) starting
 * 2-9, then four digits.
 *
 * The digit ranges are the half that used to be missing. `/^\+1\d{10}$/` accepted `+11045550123` and
 * `+19111234567`, which the backend then rejected with a 400 — so the form said the number was fine,
 * the save failed, and the only explanation lived in a snack bar. Mirrors `E164_NANP_PATTERN` in
 * welby-micro-service (`core/client/phone/phone-region.util.ts`), deliberately as a *structural* rule
 * rather than a copy of the NANPA area-code table: the ranges are fixed by the numbering plan, while
 * the table gains members over time and belongs on the server, which owns region derivation.
 */
const E164_NANP_PATTERN = /^\+1([2-9]\d{2})([2-9]\d{2})(\d{4})$/;

/** The shape alone: `+1` and ten digits, whatever they are. What the old validator checked. */
const E164_SHAPE_PATTERN = /^\+1\d{10}$/;

/** N11 codes (211, 311, … 911) are service codes and are never assignable as an area code. */
const N11_PATTERN = /^[2-9]11$/;

/**
 * Rejects what the backend would reject, so the reason shows up under the field instead of arriving as
 * a failed save.
 *
 * Deliberately no looser than before on formatting: the backend does accept `(404) 555-0123` and
 * normalises it, but this dialog has always asked for E.164 and the placeholder, tooltip and keypress
 * filter all say so. Widening the accepted input is a separate decision from closing this gap.
 */
export const twilioPhoneNumberValidator = (): ValidatorFn => (control: AbstractControl): { [key: string]: any } | null => {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }

  // Three distinct keys, because one message cannot be true for all three causes. A single
  // `invalidTwilioPhone` covering both shape and digit ranges made the field say "the area code must
  // start with 2-9" for a pasted `404-555-0123`, which is false and sends the admin hunting for a
  // range problem when what they have is a formatting one.
  if (!E164_SHAPE_PATTERN.test(control.value)) {
    return { invalidTwilioPhone: true };
  }

  const match = E164_NANP_PATTERN.exec(control.value);
  if (!match) {
    return { invalidTwilioPhoneRange: true };
  }

  if (N11_PATTERN.test(match[1])) {
    return { serviceCodeAreaCode: true };
  }

  return null;
};
